/**
 * PDF Generator Service
 *
 * High-level API for generating label PDFs. This module handles:
 * - Layout computation (label positions on pages)
 * - QR code and barcode cache generation
 * - Text variable substitution
 * - Image/font resolution (including legacy assets)
 * - PDF export via pdf-lib
 *
 * This service is used by both:
 * - LabelEditor
 * - StartPrintDialog
 */

import { tacElementContext } from '../../utils/tac';
import type {
  Template,
  TextElement,
  ImageElement,
  QrElement,
  BarcodeElement,
  PreviewLayout,
  ThemeVariable,
  ConditionOperator,
  TextCondition,
  ImageCondition,
  ColorCondition,
  ThemeVariableType,
} from "../types";
import type { PdfLayoutPlan } from "../packages/pdf-export/src/types";
import type { FontSource } from "../packages/pdf-export/src/index";
import type { BarcodeResult } from "../barcodeGenerator";
import { exportTemplateToPdf } from "../packages/pdf-export/src/index";
import { generateQrDataUrl } from "../qrGenerator";
import { generateBarcodeDataUrl } from "../barcodeGenerator";
import {
  renderTextWithData,
  RawDataContext,
  isDateVariable,
  extractVariableKeys,
  DEFAULT_DATE_FORMAT,
  isNumberVariable,
  DEFAULT_NUMBER_DECIMAL_PLACES,
  DEFAULT_NUMBER_METHOD,
  NumberFormatEntry,
  resolveConditionalTextSource,
} from "../textFormatter";
import { getEffectiveFontFamily, normalizeLegacyIds } from "../legacyAssets";
import { matchesCondition, buildConditionTypeByKey } from "../conditions";
import { parseThemeVariableValue } from "../themeVariables";
import { prepareRenderData } from "../templateRuntime";
import { DEFAULT_FONT, pdfFontSources } from "../fontConfig";
import {
  generateRetailIdQrUrls,
  isValidPackageTag,
  type Ranges,
} from "../retailIdQr";
import {
  assertQrSizeIsSafe,
  getQrCacheKey,
  QR_DEFAULT_ERROR_CORRECTION,
} from "../qrSizing";

//Types
export interface GeneratePdfOptions {
  /** The template to render */
  template: Template;

  /** Layout configuration for page/label sizes and positions */
  layout: PreviewLayout;

  /** Number of labels to generate */
  labelCount: number;

  /** Page rotation in degrees */
  rotation?: 0 | 90 | 180 | 270;

  /** Top offset for print alignment (in layout units) */
  topOffset?: number;

  /** Left offset for print alignment (in layout units) */
  leftOffset?: number;

  /** QR code index offset (for continuing from previous batch) */
  qrIndexOffset?: number;

  /** Data for variable substitution */
  data: {
    /** Common/general data (job info, org info, etc.) */
    common?: Record<string, any>;
    /** COA/lab test data */
    coa?: Record<string, any>;
    /** Product data */
    product?: Record<string, any>;
    /** Job data */
    job?: Record<string, any>;
    /** QR code values (one per label, or will be auto-generated) */
    qrValues?: string[];
  };

  /** Pre-resolved legacy images (mongoId -> base64 data URL) */
  legacyImages?: Record<string, string>;

  /** Pre-resolved legacy fonts (mongoId -> base64 data URL) */
  legacyFonts?: Record<string, string>;

  /** Pre-resolved new asset images (blobName -> URL) */
  newAssetImages?: Record<string, string>;

  /** Pre-resolved new asset fonts (blobName -> base64 data URL) */
  newAssetFonts?: Record<string, string>;

  /** Additional font sources for PDF embedding */
  fontSources?: FontSource[];

  /** Default font family */
  defaultFont?: string;

  /** Variable examples for fallback display */
  variableExamples?: Record<string, string>;

  /** RetailID QR generation data. If provided, generates real RetailID QR URLs */
  retailIdData?: {
    /** 24-character hex string (package tag) */
    packageTag: string;
    /** Array of [start, end] ranges (inclusive) */
    ranges: number[][];
  };

  /** If true, adds a "PREVIEW USE ONLY" watermark to each page */
  isPreview?: boolean;

  /** Whether the package has an attached COA record */
  hasCoa?: boolean;
}

export interface PdfResult {
  /** Raw PDF bytes */
  pdfBytes: Uint8Array;
  /** PDF as a Blob */
  blob: Blob;
  /** Blob URL for preview (call revokePdfUrl when done) */
  url: string;
  /** Number of pages in the PDF */
  pageCount: number;
  /** Number of labels per page */
  labelsPerPage: number;
}

export interface ComputedLayout {
  pageWidthPx: number;
  pageHeightPx: number;
  labelAreaWidthPx: number;
  labelAreaHeightPx: number;
  positions: { x: number; y: number }[];
  labelsPerPage: number;
  pageCount: number;
  pageRotation?: 0 | 90 | 180 | 270;
  // Label rotation (applied before print layout positioning)
  labelRotation?: 0 | 90 | 180 | 270;
  // Additional info for display
  selectedLayout: PreviewLayout;
  unit: "in" | "cm";
  pageWidth: number;
  pageHeight: number;
}

//Unit Conversion
const DPI = 72;

export function realToPixels(value: number, unit: "in" | "cm"): number {
  if (unit === "cm") {
    return (value / 2.54) * DPI;
  }
  return value * DPI;
}

//Layout

export interface ComputeLayoutOptions {
  layout: PreviewLayout;
  labelCount: number;
  templateWidth: number;
  templateHeight: number;
  topOffset?: number;
  leftOffset?: number;
  rotation?: 0 | 90 | 180 | 270;
}

//compute label positions on pages based on layout configuration.
export function computeLayout(options: ComputeLayoutOptions): ComputedLayout {
  const {
    layout,
    labelCount,
    templateWidth,
    templateHeight,
    topOffset = 0,
    leftOffset = 0,
    rotation = 0,
  } = options;

  const unit = layout.unit || "in";
  const pageWidth = layout.pageWidth;
  const pageHeight = layout.pageHeight;
  const labelAreaWidth = layout.labelWidth || templateWidth;
  const labelAreaHeight = layout.labelHeight || templateHeight;
  const spacingX = layout.spacingX || 0;
  const spacingY = layout.spacingY || 0;
  const marginTop = (layout.marginTop || 0) + topOffset;
  const marginRight = layout.marginRight || 0;
  const marginBottom = layout.marginBottom || 0;
  const marginLeft = (layout.marginLeft || 0) + leftOffset;

  // When label is rotated 90° or 270°, swap width/height for layout grid calculation
  const labelRotation = layout.labelRotation || 0;
  const effectiveLabelWidth =
    labelRotation === 90 || labelRotation === 270
      ? labelAreaHeight
      : labelAreaWidth;
  const effectiveLabelHeight =
    labelRotation === 90 || labelRotation === 270
      ? labelAreaWidth
      : labelAreaHeight;

  const pageWidthPx = realToPixels(pageWidth, unit);
  const pageHeightPx = realToPixels(pageHeight, unit);
  const labelAreaWidthPx = realToPixels(labelAreaWidth, unit);
  const labelAreaHeightPx = realToPixels(labelAreaHeight, unit);
  const effectiveLabelWidthPx = realToPixels(effectiveLabelWidth, unit);
  const effectiveLabelHeightPx = realToPixels(effectiveLabelHeight, unit);
  const spacingXPx = realToPixels(spacingX, unit);
  const spacingYPx = realToPixels(spacingY, unit);
  const marginTopPx = realToPixels(marginTop, unit);
  const marginRightPx = realToPixels(marginRight, unit);
  const marginBottomPx = realToPixels(marginBottom, unit);
  const marginLeftPx = realToPixels(marginLeft, unit);

  // When pitch is available (from legacy/preset layouts), use it as the authoritative
  // stride between label positions. This matches the legacy pdfmake grid behavior where
  // cell width = horizontalPitch * ppi. Custom layouts never have pitch set, so they
  // fall back to labelWidth + spacingX (existing behavior, unchanged).
  const strideXPx = layout.horizontalPitch
    ? realToPixels(layout.horizontalPitch, unit)
    : effectiveLabelWidthPx + spacingXPx;
  const strideYPx = layout.verticalPitch
    ? realToPixels(layout.verticalPitch, unit)
    : effectiveLabelHeightPx + spacingYPx;

  const positions: { x: number; y: number }[] = [];
  const maxColumns = layout.columns || Number.POSITIVE_INFINITY;
  const maxRows = layout.rows || Number.POSITIVE_INFINITY;

  let row = 0;
  let y = marginTopPx;
  // Use effective (post-rotation) dimensions for layout grid
  while (
    y + effectiveLabelHeightPx <= pageHeightPx - marginBottomPx + 0.01 &&
    row < maxRows
  ) {
    let col = 0;
    let x = marginLeftPx;
    while (
      x + effectiveLabelWidthPx <= pageWidthPx - marginRightPx + 0.01 &&
      col < maxColumns
    ) {
      positions.push({ x, y });
      col += 1;
      x += strideXPx;
    }
    row += 1;
    y += strideYPx;
  }

  if (!positions.length) {
    positions.push({ x: marginLeftPx, y: marginTopPx });
  }

  const labelsPerPage = Math.max(1, positions.length);
  const pageCount = Math.max(1, Math.ceil(labelCount / labelsPerPage));

  return {
    selectedLayout: layout,
    unit,
    pageWidth,
    pageHeight,
    pageWidthPx,
    pageHeightPx,
    // Return original (pre-rotation) label dimensions - the PDF renderer needs these
    labelAreaWidthPx,
    labelAreaHeightPx,
    positions,
    labelsPerPage,
    pageCount,
    pageRotation: rotation,
    labelRotation: layout.labelRotation,
  };
}

//Condition Matching

interface ConditionContext {
  data: Record<string, any>;
  variableExamples?: Record<string, string>;
  conditionTypeByKey?: Record<string, ThemeVariableType>;
  rawDataContext?: RawDataContext;
}

function resolveConditionValue(
  key: string,
  data: Record<string, any>,
  type: ThemeVariableType | undefined,
  variableExamples?: Record<string, string>,
  rawDataContext?: RawDataContext,
): unknown {
  const rendered = renderTextWithData(
    `{{${key}}}`,
    data,
    variableExamples,
    rawDataContext,
  );
  if (!rendered || rendered.includes("{{")) return undefined;
  if (type === "number") {
    const n = Number(rendered);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === "boolean") return parseThemeVariableValue(rendered, "boolean");
  return rendered;
}

function findMatchingCondition<
  T extends {
    variableKey: string;
    operator: ConditionOperator;
    value?: string;
  },
>(conditions: T[] | undefined, ctx: ConditionContext): T | undefined {
  if (!conditions || !conditions.length) return undefined;
  for (const condition of conditions) {
    const type = ctx.conditionTypeByKey?.[condition.variableKey];
    const value = resolveConditionValue(
      condition.variableKey,
      ctx.data,
      type,
      ctx.variableExamples,
      ctx.rawDataContext,
    );
    if (matchesCondition(value, condition, type)) return condition;
  }
  return undefined;
}

export function createResolveElementColor(ctx: ResolverContext) {
  return (el: any, field: string): string | undefined => {
    const conditions: ColorCondition[] | undefined = el[field];
    if (!conditions || !conditions.length) return undefined;
    const match = findMatchingCondition<ColorCondition>(conditions, {
      data: ctx.data,
      conditionTypeByKey: ctx.conditionTypeByKey,
      variableExamples: ctx.variableExamples,
      rawDataContext: ctx.rawDataContext,
    });
    return match?.color;
  };
}

function getGeneratedDateFallbackVariable(
  element: TextElement,
  condition: TextCondition,
  themeVariables?: ThemeVariable[],
) : ThemeVariable | undefined {
  if (
    !element.generatedDateVariable
    || !element.dateKind
    || condition.variableKey !== element.variableKey
    || condition.operator !== "isEmpty"
  ) {
    return undefined;
  }

  const keys = extractVariableKeys(condition.text);
  return themeVariables?.find((variable) =>
    variable.generated
    && variable.dateKind === element.dateKind
    && keys.includes(variable.key),
  );
}

//Content Resolvers
export interface ResolverContext {
  data: Record<string, any>;
  rawDataContext?: RawDataContext;
  variableExamples?: Record<string, string>;
  conditionTypeByKey?: Record<string, ThemeVariableType>;
  themeVariables?: ThemeVariable[];
  legacyImages?: Record<string, string>;
  legacyFonts?: Record<string, string>;
  newAssetImages?: Record<string, string>;
  newAssetFonts?: Record<string, string>;
  defaultFont?: string;
  isPreview?: boolean;
  hasCoa?: boolean;
}

// Create a formatTextContent function with closed-over context
export function createFormatTextContent(ctx: ResolverContext) {
  return (el: TextElement, labelData?: Record<string, any>, onSourceResolved?: (source: string) => void): string => {
    const { data: effectiveData, rawDataContext } = tacElementContext(
      el, labelData || ctx.data, ctx.rawDataContext, labelData ? ctx.rawDataContext?.theme : undefined);
    const conditions = el.conditions?.filter((condition) => {
      const fallbackVariable = getGeneratedDateFallbackVariable(
        el,
        condition,
        ctx.themeVariables,
      );
      if (!fallbackVariable) return true;
      if (fallbackVariable.dateState?.provenance === "manual") return true;
      return ctx.hasCoa !== false;
    });
    const match = findMatchingCondition<TextCondition>(conditions, {
      data: effectiveData,
      variableExamples: ctx.variableExamples,
      conditionTypeByKey: ctx.conditionTypeByKey,
      rawDataContext,
    });
    const source = resolveConditionalTextSource(match?.text, el.text, el.variableKey);
    onSourceResolved?.(source);

    const dateFormats: Record<string, string> = {};
    const effectiveDateFormat = el.dateFormat || DEFAULT_DATE_FORMAT;

    // Apply date format to the explicit variableKey if it's a date variable
    if (el.variableKey && isDateVariable(el.variableKey, ctx.themeVariables)) {
      dateFormats[el.variableKey] = effectiveDateFormat;
    }
    // Apply date format to all date variables found in the text
    const keysInText = extractVariableKeys(source || "");
    for (const key of keysInText) {
      if (isDateVariable(key, ctx.themeVariables) && !dateFormats[key]) {
        dateFormats[key] = effectiveDateFormat;
      }
    }

    const numberFormats: Record<string, NumberFormatEntry> = {};
    const effectiveDecimalPlaces =
      el.numberDecimalPlaces ?? DEFAULT_NUMBER_DECIMAL_PLACES;
    const effectiveMethod = el.numberFormatMethod ?? DEFAULT_NUMBER_METHOD;

    if (effectiveDecimalPlaces !== -1) {
      if (
        el.variableKey &&
        isNumberVariable(el.variableKey, ctx.themeVariables)
      ) {
        numberFormats[el.variableKey] = {
          decimalPlaces: effectiveDecimalPlaces,
          method: effectiveMethod,
        };
      }
      for (const key of keysInText) {
        if (isNumberVariable(key, ctx.themeVariables) && !numberFormats[key]) {
          numberFormats[key] = {
            decimalPlaces: effectiveDecimalPlaces,
            method: effectiveMethod,
          };
        }
      }
    }

    let result = renderTextWithData(
      source || "",
      effectiveData,
      ctx.variableExamples,
      rawDataContext,
      {
        dateFormats,
        numberFormats,
        isPreview: ctx.isPreview,
        themeVariables: ctx.themeVariables,
      },
    );
    // Guard against non-string return values so `.toLowerCase`/`.toUpperCase` never throw.
    if (typeof result !== "string") {
      result = result == null ? "" : String(result);
    }
    const transform = el.textTransform;
    if (transform && transform !== "none") {
      if (transform === "lowercase") result = result.toLowerCase();
      else if (transform === "uppercase") result = result.toUpperCase();
    }
    return result;
  };
}

//Create a resolveImageUrl function with closed-over context
export function createResolveImageUrl(ctx: ResolverContext, options: { strictConditionalImage?: boolean } = {}) {
  const resolveSource = createResolveImageSource(ctx, options);
  return (el: ImageElement, labelData?: Record<string, any>) => resolveSource(el, labelData).url;
}

export function createResolveImageSource(ctx: ResolverContext, options: { strictConditionalImage?: boolean } = {}) {
  return (
    el: ImageElement,
    labelData?: Record<string, any>,
  ): { url?: string; asset?: ImageElement['imageAsset'] } => {
    const effectiveData = labelData || ctx.data;
    const match = findMatchingCondition<ImageCondition>(el.conditions, {
      data: effectiveData,
      variableExamples: ctx.variableExamples,
      conditionTypeByKey: ctx.conditionTypeByKey,
      rawDataContext: ctx.rawDataContext,
    });

    if (match) {
      const condAssetBlobName = match.imageAsset?.blobName;
      if (condAssetBlobName && ctx.newAssetImages) {
        const cachedUrl = ctx.newAssetImages[condAssetBlobName];
        if (cachedUrl) {
          return { url: cachedUrl, asset: match.imageAsset };
        }
      }
      if (match.url?.trim()?.length) {
        return { url: match.url, asset: match.imageAsset };
      }
      // A chooser preview must not substitute the default symbol when the
      // selected conditional symbol could not be loaded.
      if (options.strictConditionalImage && condAssetBlobName?.trim()) return {};
    }

    // Check for new asset image first (uploaded via WYSIWYG)
    const blobName = el.imageAsset?.blobName;
    if (blobName && ctx.newAssetImages) {
      const cachedUrl = ctx.newAssetImages[blobName];
      if (cachedUrl) {
        return { url: cachedUrl, asset: el.imageAsset };
      }
    }

    // Check for legacy image asset
    const rawMongoId = el["image-asset"]?.["mongo-id"];
    if (rawMongoId && ctx.legacyImages) {
      const ids = normalizeLegacyIds(rawMongoId);
      for (const mongoId of ids) {
        const cachedUrl = ctx.legacyImages[mongoId];
        if (cachedUrl) {
          return { url: cachedUrl };
        }
      }
    }

    return { url: el.url || "" };
  };
}

export function resolveBarcodeValue(
  el: BarcodeElement,
  data: Record<string, any>,
  variableExamples?: Record<string, string>,
  rawDataContext?: RawDataContext,
  opts?: { isPreview?: boolean },
): string {
  if (el.dataSource === "productUpc") {
    const productUpc = rawDataContext?.product?.inventoryUpc;
    if (productUpc && typeof productUpc === "string" && productUpc.trim()) {
      return productUpc;
    }
    //fall through to use el.data as the default value
  }
  return renderTextWithData(
    el.data,
    data,
    variableExamples,
    rawDataContext,
    opts,
  );
}

export function createResolveBarcodeData(ctx: ResolverContext) {
  return (el: BarcodeElement, labelData?: Record<string, any>): string => {
    const effectiveData = labelData || ctx.data;
    return resolveBarcodeValue(
      el,
      effectiveData,
      ctx.variableExamples,
      ctx.rawDataContext,
      {
        isPreview: ctx.isPreview,
      },
    );
  };
}

export function createResolveFontFamily(ctx: ResolverContext) {
  return (el: TextElement): string => {
    return getEffectiveFontFamily(
      el,
      ctx.legacyFonts || {},
      ctx.defaultFont || DEFAULT_FONT,
      ctx.newAssetFonts,
    );
  };
}

//Cache Key Generation
export function getQrKey(
  value: string,
  el: QrElement,
  fg?: string,
  bg?: string,
  dpi = 72,
): string {
  return getQrCacheKey({
    value,
    width: el.width,
    height: el.height,
    margin: el.margin,
    fg: fg ?? el.fg,
    bg: bg ?? el.bg,
    errorCorrectionLevel: el.errorCorrectionLevel,
    dpi,
  });
}

export function getBarcodeKey(
  value: string,
  el: BarcodeElement,
  lineColor?: string,
  background?: string,
): string {
  return `${value}|${el.format}|${el.barWidth}|${el.height}|${el.margin}|${lineColor ?? el.lineColor}|${background ?? el.background}`;
}

//Cache Generation
export interface CacheGenerationOptions {
  template: Template;
  labelCount: number;
  qrValues: string[];
  qrIndexOffset: number;
  data: Record<string, any>;
  variableExamples?: Record<string, string>;
  rawDataContext?: RawDataContext;
  isPreview?: boolean;
  resolveElementColor?: (el: any, field: string) => string | undefined;
  conditionTypeByKey?: Record<string, any>;
}

function getQrValueForIndex(
  globalIdx: number,
  qrValues: string[],
  qrIndexOffset: number,
  qrElements: QrElement[],
): { qrIdx: number; qrValue: string } {
  const qrIdx = globalIdx + qrIndexOffset;
  const qrValue = qrValues.length
    ? (qrValues[qrIdx] ?? qrValues[qrValues.length - 1])
    : (qrElements[0]?.data as string | undefined) || `QR-${qrIdx}`;
  return { qrIdx, qrValue };
}

//Generate QR code cache for all labels
export async function generateQrCache(
  options: CacheGenerationOptions,
): Promise<Record<string, string>> {
  const { template, labelCount, qrValues, qrIndexOffset, resolveElementColor } =
    options;
  const qrElements =
    (template.elements?.filter((e) => e.type === "qr") as QrElement[]) || [];

  if (qrElements.length === 0) return {};

  const cache: Record<string, string> = {};
  const tasks: Array<() => Promise<void>> = [];

  for (let i = 0; i < labelCount; i += 1) {
    const { qrValue } = getQrValueForIndex(
      i,
      qrValues,
      qrIndexOffset,
      qrElements,
    );
    for (const qrEl of qrElements) {
      const resolvedFg = resolveElementColor?.(qrEl, "fgConditions") || qrEl.fg;
      const resolvedBg = resolveElementColor?.(qrEl, "bgConditions") || qrEl.bg;
      const key = getQrKey(qrValue, qrEl, resolvedFg, resolvedBg, template.dpi ?? 72);
      if (!(key in cache)) {
        // Mark as pending to avoid duplicate generation
        cache[key] = "";
        const capturedKey = key;
        tasks.push(async () => {
          try {
            cache[capturedKey] = await generateQrDataUrl({
              value: qrValue,
              width: qrEl.width,
              height: qrEl.height,
              marginPx: qrEl.margin,
              fg: resolvedFg,
              bg: resolvedBg,
              image: qrEl.image,
              imageWidth: qrEl.imageWidth,
              errorCorrectionLevel: qrEl.errorCorrectionLevel,
              dpi: template.dpi ?? 72,
            });
          } catch (err) {
            delete cache[capturedKey];
            throw err;
          }
        });
      }
    }
  }

  //process in batches
  const BATCH_SIZE = 500;
  for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
    const batch = tasks.slice(i, i + BATCH_SIZE);
    await Promise.all(batch.map((fn) => fn()));
  }
  return cache;
}

export async function generateQrCacheForReel(
  options: CacheGenerationOptions,
  sharedCache: Record<string, string>,
): Promise<Record<string, string>> {
  const { template, labelCount, qrValues, qrIndexOffset, resolveElementColor } =
    options;
  const qrElements =
    (template.elements?.filter((e) => e.type === "qr") as QrElement[]) || [];

  if (qrElements.length === 0) return {};

  const reelCache: Record<string, string> = {};
  const tasks: Array<() => Promise<void>> = [];

  for (let i = 0; i < labelCount; i += 1) {
    const { qrValue } = getQrValueForIndex(
      i,
      qrValues,
      qrIndexOffset,
      qrElements,
    );
    for (const qrEl of qrElements) {
      const resolvedFg = resolveElementColor?.(qrEl, "fgConditions") || qrEl.fg;
      const resolvedBg = resolveElementColor?.(qrEl, "bgConditions") || qrEl.bg;
      const key = getQrKey(qrValue, qrEl, resolvedFg, resolvedBg, template.dpi ?? 72);
      if (key in reelCache) continue; //already handled for this reel

      if (key in sharedCache && sharedCache[key] !== "") {
        reelCache[key] = sharedCache[key];
      } else {
        sharedCache[key] = "";
        reelCache[key] = "";
        const capturedKey = key;
        tasks.push(async () => {
          try {
            const url = await generateQrDataUrl({
              value: qrValue,
              width: qrEl.width,
              height: qrEl.height,
              marginPx: qrEl.margin,
              fg: resolvedFg,
              bg: resolvedBg,
              image: qrEl.image,
              imageWidth: qrEl.imageWidth,
              errorCorrectionLevel: qrEl.errorCorrectionLevel,
              dpi: template.dpi ?? 72,
            });
            sharedCache[capturedKey] = url;
            reelCache[capturedKey] = url;
          } catch (err) {
            delete sharedCache[capturedKey];
            delete reelCache[capturedKey];
            throw err;
          }
        });
      }
    }
  }

  const BATCH_SIZE = 500;
  for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
    const batch = tasks.slice(i, i + BATCH_SIZE);
    await Promise.all(batch.map((fn) => fn()));
  }

  return reelCache;
}

//Generate barcode cache for all labels
export function generateBarcodeCache(
  options: CacheGenerationOptions,
): Record<string, BarcodeResult> {
  const {
    template,
    labelCount,
    qrValues,
    qrIndexOffset,
    data,
    variableExamples,
    rawDataContext,
    isPreview,
    resolveElementColor,
  } = options;
  const barcodeElements =
    (template.elements?.filter(
      (e) => e.type === "barcode",
    ) as BarcodeElement[]) || [];
  const qrElements =
    (template.elements?.filter((e) => e.type === "qr") as QrElement[]) || [];

  if (barcodeElements.length === 0) return {};

  const cache: Record<string, BarcodeResult> = {};

  for (let i = 0; i < labelCount; i += 1) {
    const { qrIdx, qrValue } = getQrValueForIndex(
      i,
      qrValues,
      qrIndexOffset,
      qrElements,
    );
    const labelData = {
      ...data,
      qrIndex: qrIdx,
      qrValue,
      labelIndex: i + qrIndexOffset,
    };

    for (const bcEl of barcodeElements) {
      const resolvedData = resolveBarcodeValue(
        bcEl,
        labelData,
        variableExamples,
        rawDataContext,
        {
          isPreview,
        },
      );
      const resolvedLineColor =
        resolveElementColor?.(bcEl, "lineColorConditions") || bcEl.lineColor;
      const resolvedBackground =
        resolveElementColor?.(bcEl, "backgroundConditions") || bcEl.background;
      const key = getBarcodeKey(
        resolvedData,
        bcEl,
        resolvedLineColor,
        resolvedBackground,
      );
      if (!cache[key]) {
        cache[key] = generateBarcodeDataUrl({
          value: resolvedData,
          format: bcEl.format,
          barWidth: bcEl.barWidth,
          height: bcEl.height,
          margin: bcEl.margin,
          lineColor: resolvedLineColor,
          background: resolvedBackground,
        });
      }
    }
  }

  return cache;
}

//image Fetching

async function fetchImageBytes(url: string): Promise<Uint8Array | undefined> {
  if (!url) return undefined;

  if (url.startsWith("data:")) {
    try {
      const [, base64] = url.split(",");
      if (!base64) return undefined;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    } catch {
      return undefined;
    }
  }

  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return undefined;
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    return undefined;
  }
}

async function fetchFontBytes(
  url: string,
  baseUrl?: string,
): Promise<Uint8Array | undefined> {
  if (!url) return undefined;

  if (url.startsWith("data:")) {
    try {
      const [, base64] = url.split(",");
      if (!base64) return undefined;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    } catch {
      return undefined;
    }
  }

  let normalizedUrl = url;
  if (url.startsWith("/")) {
    const origin =
      baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
    normalizedUrl = origin ? new URL(url, origin).href : url;
  }

  try {
    const res = await fetch(normalizedUrl, { mode: "cors" });
    if (!res.ok) return undefined;
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    return undefined;
  }
}

// Main PDF Generation
/**
 * Generate a PDF with labels based on the provided options.
 *
 * This is the main entry point for PDF generation. It handles:
 * - Layout computation
 * - QR and barcode cache generation
 * - Variable substitution
 * - Legacy asset resolution
 * - PDF export
 *
 * @example
 * ```typescript
 * const result = await generateLabelPdf({
 *   template: myTemplate,
 *   layout: selectedLayout,
 *   labelCount: 10,
 *   data: {
 *     coa: coaData,
 *     qrValues: ['QR-001', 'QR-002', ...],
 *   },
 *   legacyImages: preloadedImages,
 *   legacyFonts: preloadedFonts,
 * });
 *
 * // Display in iframe
 * iframe.src = result.url;
 *
 * // Cleanup when done
 * revokePdfUrl(result.url);
 * ```
 */
export async function generateLabelPdf(
  options: GeneratePdfOptions,
): Promise<PdfResult> {
  const {
    template,
    layout,
    labelCount,
    rotation = 0,
    topOffset = 0,
    leftOffset = 0,
    qrIndexOffset = 0,
    data,
    legacyImages = {},
    legacyFonts = {},
    newAssetImages = {},
    newAssetFonts = {},
    fontSources = pdfFontSources,
    defaultFont = DEFAULT_FONT,
    variableExamples = {},
    isPreview = false,
  } = options;

  //prepare all render data using the centralized utility
  const { fullData, themeData, rawDataContext } = prepareRenderData({
    template,
    coa: data.coa,
    job: data.job,
    product: data.product,
    common: data.common,
  });

  console.log("moss context", rawDataContext, "from", data);

  //build condition type lookup from theme variables + component variable catalog
  const conditionTypeByKey = buildConditionTypeByKey(template.themeVariables);

  // Generate QR values - prefer RetailID URLs if data is available
  let qrValues: string[] = [];
  if (
    options.retailIdData?.packageTag &&
    options.retailIdData?.ranges?.length
  ) {
    const { packageTag, ranges } = options.retailIdData;
    if (isValidPackageTag(packageTag)) {
      qrValues = generateRetailIdQrUrls({
        packageTag,
        ranges: ranges as Ranges,
      });
    }
  }
  // Fall back to provided qrValues or empty array
  if (qrValues.length === 0) {
    qrValues = data.qrValues || [];
  }

  const qrElements = (template.elements?.filter((e) => e.type === 'qr') as QrElement[]) || [];

  // Preflight only values this render will use. Print runs may include values for later reels.
  const computedLayout = computeLayout({
    layout,
    labelCount,
    templateWidth: template.width,
    templateHeight: template.height,
    topOffset,
    leftOffset,
    rotation,
  });

  let effectiveLabelCount = labelCount;
  if (isPreview && labelCount > computedLayout.labelsPerPage) {
    effectiveLabelCount = computedLayout.labelsPerPage;
  }

  for (let i = 0; i < effectiveLabelCount; i += 1) {
    const { qrValue } = getQrValueForIndex(i, qrValues, qrIndexOffset, qrElements);
    for (const qrEl of qrElements) {
      assertQrSizeIsSafe({
        value: qrValue,
        requestedSize: Math.min(qrEl.width, qrEl.height),
        dpi: template.dpi ?? 72,
        margin: qrEl.margin,
        errorCorrectionLevel: qrEl.errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
      });
    }
  }

  const effectivePageCount = Math.max(
    1,
    Math.ceil(effectiveLabelCount / computedLayout.labelsPerPage),
  );

  const resolverCtx: ResolverContext = {
    data: fullData,
    rawDataContext,
    variableExamples,
    conditionTypeByKey,
    themeVariables: template.themeVariables,
    legacyImages,
    legacyFonts,
    newAssetImages,
    newAssetFonts,
    defaultFont,
    isPreview,
    hasCoa: options.hasCoa,
  };

  const formatTextContent = createFormatTextContent(resolverCtx);
  const resolveImageUrl = createResolveImageUrl(resolverCtx);
  const resolveBarcodeData = createResolveBarcodeData(resolverCtx);
  const resolveFontFamily = createResolveFontFamily(resolverCtx);
  const resolveElementColor = createResolveElementColor(resolverCtx);

  const cacheOptions: CacheGenerationOptions = {
    template,
    labelCount: effectiveLabelCount,
    qrValues,
    qrIndexOffset,
    data: fullData,
    variableExamples,
    rawDataContext,
    isPreview,
    resolveElementColor,
    conditionTypeByKey,
  };

  const qrCache = {};
  const barcodeCache = generateBarcodeCache(cacheOptions);

  const layoutPlan: PdfLayoutPlan = {
    pageWidthPx: computedLayout.pageWidthPx,
    pageHeightPx: computedLayout.pageHeightPx,
    labelAreaWidthPx: computedLayout.labelAreaWidthPx,
    labelAreaHeightPx: computedLayout.labelAreaHeightPx,
    positions: computedLayout.positions,
    labelsPerPage: computedLayout.labelsPerPage,
    pageCount: effectivePageCount,
    pageRotation: computedLayout.pageRotation,
    labelRotation: computedLayout.labelRotation,
  };

  //create resolveFontData for legacy and new asset fonts
  const resolveFontData = async (
    family: string,
  ): Promise<Uint8Array | undefined> => {
    const unquotedFamily = family.replace(/^['"]|['"]$/g, "");

    // Check for new asset fonts (format: asset-font-{fileId}-{name})
    const assetMatch = unquotedFamily.match(/^asset-font-(\d+)-(.+)$/);
    if (assetMatch) {
      const fileId = parseInt(assetMatch[1], 10);
      const textElement = template.elements?.find(
        (el) =>
          el.type === "text" &&
          (el as TextElement).fontAsset?.fileId === fileId,
      ) as TextElement | undefined;

      if (textElement?.fontAsset?.blobName) {
        const blobName = textElement.fontAsset.blobName;
        const base64 = newAssetFonts[blobName];
        if (base64) {
          try {
            const base64Data = base64.split(",")[1];
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
          } catch (e) {
            console.warn(`[PDF] Failed to decode asset font ${blobName}:`, e);
            return undefined;
          }
        }
      }
    }

    // Check for legacy fonts (format: legacy-font-{mongoId})
    const legacyMatch = unquotedFamily.match(/^legacy-font-(.+)$/);
    if (legacyMatch) {
      const mongoId = legacyMatch[1];
      const base64 = legacyFonts[mongoId];
      if (base64) {
        try {
          const base64Data = base64.split(",")[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        } catch (e) {
          console.warn(`[PDF] Failed to decode legacy font ${mongoId}:`, e);
          return undefined;
        }
      }
    }
    return undefined;
  };

  //export to PDF
  const result = await exportTemplateToPdf({
    template,
    layout: layoutPlan,
    labelCount: effectiveLabelCount,
    qrValues,
    qrIndexOffset,
    commonData: fullData,
    themeData,
    qrElements,
    qrCache,
    barcodeCache,
    getQrKey: (value, el, fg, bg) => getQrKey(value, el, fg, bg, template.dpi ?? 72),
    getBarcodeKey,
    formatTextContent,
    resolveImageUrl,
    resolveImageSource: createResolveImageSource(resolverCtx),
    resolveBarcodeData,
    fetchImageBytes,
    fontSources,
    fetchFontBytes: (url: string) => fetchFontBytes(url),
    resolveFontFamily: (el) => resolveFontFamily(el as TextElement),
    resolveFontData,
    defaultFontFamily: defaultFont,
    resolveElementColor,
    isPreview,
  });

  const url = URL.createObjectURL(result.blob);

  return {
    pdfBytes: result.pdfBytes,
    blob: result.blob,
    url,
    pageCount: computedLayout.pageCount,
    labelsPerPage: computedLayout.labelsPerPage,
  };
}

/**
 * Revoke a PDF blob URL to free memory.
 * Call this when the PDF preview is no longer needed.
 */
export function revokePdfUrl(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // Ignore errors
  }
}

export { DEFAULT_FONT, pdfFontSources };

export function createFetchFontBytes(baseUrl: string) {
  return (url: string) => fetchFontBytes(url, baseUrl);
}

export { fetchImageBytes };
